/**
 * Bulk Device Importer — Control Onboarding Step 1
 * Supports: CSV paste / CSV file upload / Barcode-QR camera scan
 */
import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  Upload, ScanLine, Loader2, CheckCircle, X, FileText,
  Camera, Plus, Trash2, Download, Printer
} from 'lucide-react';
import { getBearerToken } from '@/context/TrackOticAuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

const CSV_HEADERS = ['imei_number', 'device_type', 'manufacturer', 'sim_number', 'module_sn'];
const CSV_EXAMPLE = `imei_number,device_type,manufacturer,sim_number,module_sn
356938101010101,PRO,Quectel,8991000001,MOD-001
356938101010102,PRO,Quectel,8991000002,MOD-002
356938101010103,HUB,SIMCom,,
356938101010104,LITE_PRE,Quectel,8991000003,MOD-004
356938101010105,LITE,Quectel,,`;

function parseCSV(text) {
  const rows = text.trim().split(/\r?\n/).filter(Boolean);
  if (rows.length === 0) return { rows: [], errors: ['Empty input'] };
  const errors = [];

  // Detect header row
  const firstRow = rows[0].toLowerCase();
  const hasHeader = CSV_HEADERS.some(h => firstRow.includes(h));
  const dataRows = hasHeader ? rows.slice(1) : rows;

  // Header map (allow column reordering if header present)
  let headers = CSV_HEADERS;
  if (hasHeader) {
    headers = rows[0].split(',').map(h => h.trim().toLowerCase().replace(/[^a-z_]/g, ''));
  }

  const out = [];
  dataRows.forEach((line, i) => {
    const cells = line.split(',').map(c => c.trim());
    const row = {};
    headers.forEach((h, idx) => { row[h] = cells[idx] || ''; });
    if (!row.imei_number) {
      errors.push(`Row ${i + (hasHeader ? 2 : 1)}: missing IMEI`);
      return;
    }
    row.device_type = (row.device_type || 'PRO').toUpperCase();
    if (!['PRO', 'HUB', 'LITE', 'LITE_PRE', 'LITE-PRE'].includes(row.device_type)) {
      errors.push(`Row ${i + (hasHeader ? 2 : 1)}: invalid device_type '${row.device_type}' (use PRO/HUB/LITE/LITE_PRE)`);
      return;
    }
    if (row.device_type === 'LITE-PRE') row.device_type = 'LITE_PRE';
    out.push(row);
  });
  return { rows: out, errors };
}

export default function BulkDeviceImporter({ onComplete, onSingleSwitch }) {
  const [mode, setMode] = useState('csv'); // csv | scan
  const [csvText, setCsvText] = useState('');
  const [parsedRows, setParsedRows] = useState([]);
  const [parseErrors, setParseErrors] = useState([]);
  const [scanned, setScanned] = useState([]); // [{imei, device_type}]
  const [scanType, setScanType] = useState('PRO');
  const [scanning, setScanning] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);

  const token = getBearerToken();
  const headers = { Authorization: `Bearer ${token}` };

  // Re-parse whenever csvText changes
  useEffect(() => {
    if (!csvText.trim()) {
      setParsedRows([]); setParseErrors([]);
      return;
    }
    const { rows, errors } = parseCSV(csvText);
    setParsedRows(rows);
    setParseErrors(errors);
  }, [csvText]);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result);
    reader.readAsText(file);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_EXAMPLE], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'trackotic_device_template.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ====== Scanner (html5-qrcode) ======
  const startScanner = async () => {
    setScanning(true);
    try {
      const mod = await import('html5-qrcode');
      const Html5Qrcode = mod.Html5Qrcode;
      // Wait for DOM
      await new Promise(r => setTimeout(r, 100));
      const scanner = new Html5Qrcode('imei-scanner-region');
      scannerRef.current = scanner;
      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 280, height: 140 }, aspectRatio: 1.7 },
        (decodedText) => {
          const clean = decodedText.replace(/\D/g, '');
          if (clean.length < 14 || clean.length > 16) {
            toast.error(`Invalid IMEI length: ${clean.length} digits`);
            return;
          }
          setScanned(prev => {
            if (prev.some(s => s.imei_number === clean)) {
              toast.info(`Already scanned: ${clean}`);
              return prev;
            }
            toast.success(`Scanned: ${clean}`);
            return [...prev, { imei_number: clean, device_type: scanType }];
          });
        },
        () => { /* per-frame parse error — safe to ignore */ }
      );
    } catch (e) {
      console.error('[BulkImporter] scanner start failed:', e);
      toast.error('Camera unavailable. Use CSV mode instead.');
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
        scannerRef.current = null;
      }
    } catch (e) {
       
      // scanner stop errors when already stopped — safe to ignore
    }
    setScanning(false);
  };

  useEffect(() => () => { stopScanner(); }, []);

  const removeScanned = (imei) => setScanned(prev => prev.filter(s => s.imei_number !== imei));

  // ====== Submit ======
  const submit = async () => {
    const devices = mode === 'csv' ? parsedRows : scanned;
    if (devices.length === 0) { toast.error('No devices to import'); return; }
    try {
      setSubmitting(true);
      await stopScanner();
      const res = await axios.post(
        `${API_BASE}/api/v1/control/onboarding/step1-bulk-receive`,
        { devices },
        { headers }
      );
      setResult(res.data);
      toast.success(res.data.message);
      if (onComplete) onComplete(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk import failed');
    } finally {
      setSubmitting(false);
    }
  };

  const reset = () => {
    setCsvText(''); setParsedRows([]); setParseErrors([]);
    setScanned([]); setResult(null);
  };

  /**
   * POST the registered device UIDs to the print endpoint, receive a PDF blob,
   * and open it in a new tab. Cookie auth carries credentials.
   */
  const handlePrintStickers = async () => {
    const uids = (result?.registered || []).map(r => r.device_uid).filter(Boolean);
    if (uids.length === 0) {
      toast.error('No registered devices to print');
      return;
    }
    try {
      const resp = await axios.post(
        `${API_BASE}/api/v1/control/print/device-stickers`,
        { device_uids: uids },
        { responseType: 'blob' },
      );
      const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      // Revoke after the new tab has had time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error('Could not generate sticker PDF');
    }
  };

  const inputCls = "bg-white dark:bg-slate-900 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500";

  if (result) {
    return (
      <div className="space-y-4">
        <div className="bg-emerald-50 dark:bg-green-900/20 border border-emerald-200 dark:border-green-700 rounded-lg p-4 text-center">
          <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-2" />
          <p className="text-green-700 dark:text-green-400 font-semibold">{result.message}</p>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
            <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{result.registered_count}</p>
            <p className="text-xs text-slate-500 uppercase">Registered</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
            <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{result.skipped_duplicates}</p>
            <p className="text-xs text-slate-500 uppercase">Duplicates</p>
          </div>
          <div className="bg-slate-50 dark:bg-slate-900 rounded-lg p-3">
            <p className="text-2xl font-bold text-rose-600 dark:text-rose-400">{result.failed_count}</p>
            <p className="text-xs text-slate-500 uppercase">Failed</p>
          </div>
        </div>
        {result.failed?.length > 0 && (
          <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg p-3 text-sm">
            <p className="text-rose-700 dark:text-rose-300 font-semibold mb-1">Failed rows:</p>
            <ul className="text-rose-600 dark:text-rose-400 text-xs space-y-1 max-h-40 overflow-y-auto">
              {result.failed.map((f) => <li key={`${f.imei || 'empty'}-${f.reason}`}>• {f.imei} — {f.reason}</li>)}
            </ul>
          </div>
        )}
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={reset} className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300" data-testid="import-another-btn">
            <Plus className="w-4 h-4 mr-1" />Import Another Batch
          </Button>
          {result.registered_count > 0 && (
            <Button
              onClick={handlePrintStickers}
              variant="outline"
              className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/20"
              data-testid="print-qr-stickers-btn"
            >
              <Printer className="w-4 h-4 mr-1" />Print QR Stickers ({result.registered_count})
            </Button>
          )}
          <Button onClick={() => onComplete?.(result)} className="bg-teal-600 hover:bg-teal-700 flex-1 text-white" data-testid="continue-to-step2-btn">
            Continue to Step 2
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bulk-device-importer">
      {/* Mode toggle */}
      <div className="flex gap-2 flex-wrap">
        <Button size="sm" onClick={onSingleSwitch}
          variant="outline"
          className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300"
          data-testid="single-mode-btn">
          <Plus className="w-4 h-4 mr-1" />Single Device
        </Button>
        <Button size="sm" onClick={() => { stopScanner(); setMode('csv'); }}
          className={mode === 'csv' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}
          data-testid="csv-mode-btn">
          <FileText className="w-4 h-4 mr-1" />Bulk CSV
        </Button>
        <Button size="sm" onClick={() => setMode('scan')}
          className={mode === 'scan' ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300'}
          data-testid="scan-mode-btn">
          <ScanLine className="w-4 h-4 mr-1" />Scan Barcode/QR
        </Button>
      </div>

      {/* CSV Mode */}
      {mode === 'csv' && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()}
              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300" data-testid="upload-csv-btn">
              <Upload className="w-4 h-4 mr-1" />Upload CSV File
            </Button>
            <input ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleFileUpload} className="hidden" />
            <Button size="sm" variant="outline" onClick={downloadTemplate}
              className="border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300" data-testid="download-template-btn">
              <Download className="w-4 h-4 mr-1" />Download Template
            </Button>
          </div>
          <div>
            <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">
              Paste CSV (columns: imei_number, device_type, manufacturer, sim_number, module_sn)
            </label>
            <textarea
              value={csvText}
              onChange={e => setCsvText(e.target.value)}
              placeholder={CSV_EXAMPLE}
              className={cn(inputCls, 'w-full rounded-md border p-3 font-mono text-xs h-44 resize-y')}
              data-testid="csv-textarea"
            />
          </div>

          {parseErrors.length > 0 && (
            <div className="bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-lg p-3 text-xs">
              <p className="text-rose-700 dark:text-rose-300 font-semibold mb-1">Parse warnings:</p>
              <ul className="text-rose-600 dark:text-rose-400 space-y-0.5 max-h-24 overflow-y-auto">
                {parseErrors.slice(0, 10).map((err) => <li key={err}>• {err}</li>)}
                {parseErrors.length > 10 && <li>+ {parseErrors.length - 10} more...</li>}
              </ul>
            </div>
          )}

          {parsedRows.length > 0 && (
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-slate-700 dark:text-slate-300 font-semibold">
                  Preview: {parsedRows.length} valid device{parsedRows.length === 1 ? '' : 's'}
                </p>
                <Badge className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400">Ready to import</Badge>
              </div>
              <div className="max-h-40 overflow-y-auto text-xs font-mono">
                {parsedRows.slice(0, 20).map((r) => (
                  <div key={r.imei_number} className="flex gap-3 py-1 border-b border-slate-200 dark:border-slate-800 last:border-0">
                    <span className="text-slate-900 dark:text-slate-100 w-36">{r.imei_number}</span>
                    <span className="text-blue-600 dark:text-blue-400 w-12">{r.device_type}</span>
                    <span className="text-slate-500 dark:text-slate-500">{r.manufacturer || '—'}</span>
                  </div>
                ))}
                {parsedRows.length > 20 && <p className="text-slate-500 text-xs pt-1">+ {parsedRows.length - 20} more</p>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Scanner Mode */}
      {mode === 'scan' && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm text-slate-600 dark:text-slate-400 mb-1 block">Device Type for Scanned</label>
              <select value={scanType} onChange={e => setScanType(e.target.value)}
                className={cn(inputCls, 'w-full rounded-md border p-2.5')} data-testid="scan-device-type">
                <option value="PRO">PRO (Reusable)</option>
                <option value="HUB">HUB (Fixed)</option>
                <option value="LITE_PRE">LITE-Pre (Reusable)</option>
                <option value="LITE">LITE (Single-use)</option>
              </select>
            </div>
            <div className="flex items-end">
              {!scanning ? (
                <Button onClick={startScanner} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="start-scanner-btn">
                  <Camera className="w-4 h-4 mr-2" />Start Camera
                </Button>
              ) : (
                <Button onClick={stopScanner} variant="destructive" className="w-full" data-testid="stop-scanner-btn">
                  <X className="w-4 h-4 mr-2" />Stop Camera
                </Button>
              )}
            </div>
          </div>

          <div id="imei-scanner-region"
            className={cn(
              "rounded-lg overflow-hidden border-2 border-dashed transition-all",
              scanning
                ? "border-blue-500 bg-black min-h-[280px]"
                : "border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 min-h-[120px] flex items-center justify-center"
            )}>
            {!scanning && (
              <div className="text-center text-slate-500 dark:text-slate-500 text-sm p-6">
                <ScanLine className="w-8 h-8 mx-auto mb-2 opacity-50" />
                Point camera at IMEI barcode or QR sticker
              </div>
            )}
          </div>

          {scanned.length > 0 && (
            <div className="bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <p className="text-sm text-slate-700 dark:text-slate-300 font-semibold mb-2">
                Scanned: {scanned.length}
              </p>
              <div className="max-h-44 overflow-y-auto space-y-1">
                {scanned.map(s => (
                  <div key={s.imei_number} className="flex items-center justify-between py-1 px-2 rounded bg-white dark:bg-slate-800 text-xs">
                    <span className="font-mono text-slate-900 dark:text-slate-100">{s.imei_number}</span>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-[10px]">{s.device_type}</Badge>
                      <button onClick={() => removeScanned(s.imei_number)} className="text-rose-500 hover:text-rose-700"
                        data-testid={`remove-scan-${s.imei_number}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Submit */}
      <Button
        onClick={submit}
        disabled={submitting || (mode === 'csv' ? parsedRows.length === 0 : scanned.length === 0)}
        className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white"
        data-testid="bulk-submit-btn">
        {submitting
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>
          : <><Upload className="w-4 h-4 mr-2" />Import {mode === 'csv' ? parsedRows.length : scanned.length} Device{(mode === 'csv' ? parsedRows.length : scanned.length) === 1 ? '' : 's'}</>
        }
      </Button>
    </div>
  );
}
